import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plug, Plus, Trash2, X, Loader2, Server, Webhook, Inbox, TestTube, CheckCircle2, AlertCircle, Layers, Wrench, Edit3, Save, Info, ChevronDown, ChevronRight, Key, ExternalLink, Copy, Check } from 'lucide-react';
import { haptic } from '../../telegram';
import { integrationsApi } from '../../api/integrationsApi';
import { agentsApi } from '../../api/agentsApi';
import type { McpServerDto, InboundWebhookDto, CustomWebhook, McpToolInfo } from '../../api/integrationsApi';
import type { Agent } from '../../types/contracts';

declare const window: Window & { Telegram?: any };
type Tab = 'mcp' | 'inbound' | 'webhook' | 'catalog';
const METHODS = ['POST', 'GET', 'PUT', 'PATCH', 'DELETE'];
const CAT_LABELS: Record<string, string> = { google: 'Google', crm: 'CRM', communication: 'Почта и мессенджеры', productivity: 'Продуктивность', dev: 'Разработка', other: 'Другое' };
const CAT_ORDER = ['google', 'crm', 'communication', 'productivity', 'dev', 'other'];
const INTRO: Record<Tab, { t: string; d: string }> = {
  catalog: { t: 'Каталог сервисов', d: 'Главный способ подключить внешние сервисы — CRM, почту, таблицы и другое. Выберите категорию и подключите нужный сервис в пару кликов. Можно подключить несколько аккаунтов одного сервиса.' },
  mcp: { t: 'MCP-серверы', d: 'Подключение внешних наборов инструментов по протоколу MCP. Для продвинутых интеграций, которых пока нет в каталоге.' },
  webhook: { t: 'Исходящие вебхуки', d: 'Агент сам отправляет запрос во внешнюю систему — например, уведомление или запись в другой сервис.' },
  inbound: { t: 'Триггеры (входящие вебхуки)', d: 'Внешний сервис вызывает вашего агента по персональному URL. Агент обрабатывает событие и отвечает.' },
};

export function Connections() {
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('catalog');
  const [copied, setCopied] = useState<string | null>(null);
  const [catFilter, setCatFilter] = useState<string>('all');
  const [openInbound, setOpenInbound] = useState<string | null>(null);
  const [mcpList, setMcpList] = useState<McpServerDto[]>([]);
  const [inboundList, setInboundList] = useState<InboundWebhookDto[]>([]);
  const [webhookList, setWebhookList] = useState<CustomWebhook[]>([]);
  const [catalog, setCatalog] = useState<any[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; msg: string; tools: string[]; details: McpToolInfo[] } | null>(null);
  const [catTesting, setCatTesting] = useState<string | null>(null);
  const [catTestResult, setCatTestResult] = useState<{ id: string; ok: boolean; msg: string; tools: string[]; details: McpToolInfo[] } | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState<string | null>(null);
  const [catToolsOpen, setCatToolsOpen] = useState(false);
  const [mcpToolsOpen, setMcpToolsOpen] = useState(false);
  const [tokenFormOpen, setTokenFormOpen] = useState<string | null>(null);
  const [tfSubdomain, setTfSubdomain] = useState('');
  const [tfToken, setTfToken] = useState('');
  const [tfWebhookUrl, setTfWebhookUrl] = useState('');
  const [tfUserToken, setTfUserToken] = useState('');
  const [tfCompanyId, setTfCompanyId] = useState('');
  const [tfSaving, setTfSaving] = useState(false);
  const [tfError, setTfError] = useState('');
  const [advBusy, setAdvBusy] = useState<string | null>(null);
  const [pendingPoll, setPendingPoll] = useState<{ provider: string; connectionId: string } | null>(null);
  const [aName, setAName] = useState('');
  const [aUrl, setAUrl] = useState('');
  const [aToken, setAToken] = useState('');
  const [aAgentId, setAAgentId] = useState('');
  const [aMethod, setAMethod] = useState('POST');
  const [aDesc, setADesc] = useState('');
  const [aEnabled, setAEnabled] = useState(true);

  const loadAll = () => {
    Promise.all([
      integrationsApi.listMcp().catch(() => [] as McpServerDto[]),
      integrationsApi.listInbound().catch(() => [] as InboundWebhookDto[]),
      integrationsApi.listWebhooks().catch(() => [] as CustomWebhook[]),
      integrationsApi.getCatalog().catch(() => ({ providers: [] } as any)),
      agentsApi.list().catch(() => [] as Agent[]),
    ]).then(([m, i, w, c, ag]) => {
      setMcpList(Array.isArray(m) ? m : []); setInboundList(Array.isArray(i) ? i : []);
      setWebhookList(Array.isArray(w) ? w : []); setCatalog(c?.providers || []);
      setAgents(Array.isArray(ag) ? ag : []); setLoading(false);
    });
  };
  useEffect(() => { loadAll(); }, []);

  // Polling статуса Composio после возврата из OAuth
  useEffect(() => {
    if (!pendingPoll) return;
    let stopped = false;
    let attempts = 0;
    const poll = async () => {
      if (stopped || attempts > 20) { setPendingPoll(null); return; }
      attempts++;
      try {
        const r = await integrationsApi.getConnectionStatus(pendingPoll.provider, pendingPoll.connectionId);
        if (r?.status === 'connected') {
          setPendingPoll(null);
          haptic('heavy');
          loadAll();
          return;
        }
      } catch {}
      if (!stopped) setTimeout(poll, 3000);
    };
    // Запускаем сразу и при возврате фокуса
    poll();
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { stopped = true; document.removeEventListener('visibilitychange', onVisible); };
  }, [pendingPoll]);

  const openLink = (url: string) => { const tg = window?.Telegram?.WebApp; if (tg?.openLink) tg.openLink(url); else window.open(url, '_blank'); };
  const copy = (text: string, id: string) => { haptic('light'); if (navigator.clipboard) navigator.clipboard.writeText(text).then(() => { setCopied(id); setTimeout(() => setCopied(null), 1400); }).catch(() => {}); };
  const agentName = (id: string | null) => (id ? (agents.find((a) => a.id === id)?.name || id) : 'Без агента');

  const resetForm = () => { setAName(''); setAUrl(''); setAToken(''); setAAgentId(''); setAMethod('POST'); setADesc(''); setAEnabled(true); setError(''); };
  const openAdd = () => { haptic('light'); resetForm(); setEditId(null); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditId(null); setError(''); };
  const editMcp = (m: McpServerDto) => { haptic('light'); resetForm(); setTab('mcp'); setEditId(m.id); setAName(m.name); setAUrl(m.url); setAEnabled(m.enabled); setFormOpen(true); };
  const editWebhook = (w: CustomWebhook) => { haptic('light'); resetForm(); setTab('webhook'); setEditId(w.id); setAName(w.name); setAUrl(w.url); setAMethod(w.method || 'POST'); setADesc(w.description || ''); setAEnabled(w.enabled); setFormOpen(true); };
  const editInbound = (i: InboundWebhookDto) => { haptic('light'); resetForm(); setTab('inbound'); setEditId(i.id); setAName(i.name || ''); setAAgentId(i.agent_id || ''); setFormOpen(true); };

  const submit = async () => {
    haptic('medium'); setSaving(true); setError('');
    try {
      if (tab === 'mcp') {
        if (!aName.trim() || !aUrl.trim()) { setError('Введите имя и URL'); setSaving(false); return; }
        if (editId) await integrationsApi.updateMcp(editId, { name: aName.trim(), url: aUrl.trim(), enabled: aEnabled, ...(aToken ? { authToken: aToken } : {}) });
        else await integrationsApi.createMcp({ name: aName.trim(), url: aUrl.trim(), authToken: aToken || undefined });
      } else if (tab === 'inbound') {
        if (!aAgentId) { setError('Выберите агента'); setSaving(false); return; }
        if (editId) await integrationsApi.updateInbound(editId, { agent_id: aAgentId, name: aName.trim() });
        else await integrationsApi.createInbound({ agent_id: aAgentId, name: aName.trim() || undefined });
      } else if (tab === 'webhook') {
        if (!aName.trim() || !aUrl.trim()) { setError('Введите имя и URL'); setSaving(false); return; }
        if (editId) await integrationsApi.updateWebhook(editId, { name: aName.trim(), url: aUrl.trim(), method: aMethod, description: aDesc, enabled: aEnabled });
        else await integrationsApi.createWebhook({ name: aName.trim(), description: aDesc, url: aUrl.trim(), method: aMethod, has_headers: false, body_schema: {} });
      }
      closeForm(); resetForm(); haptic('heavy'); loadAll();
    } catch (e: any) { setError(e?.message || 'Ошибка'); } finally { setSaving(false); }
  };

  const testMcp = async (id: string) => {
    haptic('light'); setTesting(id); setTestResult(null); setMcpToolsOpen(false);
    try {
      const r = await integrationsApi.testMcp(id);
      const details = r.toolDetails || (r.tools || []).map((n) => ({ name: n, description: '' }));
      setTestResult({ id, ok: r.ok, msg: r.ok ? ('Доступно инструментов: ' + details.length) : (r.error || 'Ошибка'), tools: r.tools || [], details });
    } catch (e: any) { setTestResult({ id, ok: false, msg: e?.message || 'Ошибка', tools: [], details: [] }); } finally { setTesting(null); }
  };
  const delMcp = async (id: string) => { haptic('heavy'); try { await integrationsApi.deleteMcp(id); setMcpList((p) => p.filter((x) => x.id !== id)); } catch {} };
  const delInbound = async (id: string) => { haptic('heavy'); try { await integrationsApi.deleteInbound(id); setInboundList((p) => p.filter((x) => x.id !== id)); } catch {} };
  const toggleInbound = async (i: InboundWebhookDto) => { haptic('medium'); try { await integrationsApi.updateInbound(i.id, { enabled: !i.enabled }); setInboundList((p) => p.map((x) => x.id === i.id ? { ...x, enabled: !i.enabled } : x)); } catch {} };
  const delWebhook = async (id: string) => { haptic('heavy'); try { await integrationsApi.deleteWebhook(id); setWebhookList((p) => p.filter((x) => x.id !== id)); } catch {} };

  const connList = (c: any): any[] => (Array.isArray(c?.connections) ? c.connections : (c?.connection ? [c.connection] : []));
  const connectCatalog = async (c: any) => {
    haptic('medium'); setConnecting(c.id);
    try {
      const r = await integrationsApi.connectProvider(c.id);
      if ((r?.mode === 'oauth' || r?.mode === 'composio') && r.authorizeUrl) {
        if (r.mode === 'composio' && r.connectionId) { setPendingPoll({ provider: c.id, connectionId: r.connectionId }); }
        openLink(r.authorizeUrl);
      }
      else { haptic('heavy'); loadAll(); }
    } catch {} finally { setConnecting(null); }
  };
  const disconnectCatalog = async (conn: any) => { if (!conn?.id) return; haptic('heavy'); setConnecting(conn.id); try { await integrationsApi.deleteConnection(conn.id); loadAll(); } catch {} finally { setConnecting(null); } };
  const testCatalog = async (conn: any) => {
    if (!conn?.id) return;
    haptic('light'); setCatTesting(conn.id); setCatTestResult(null); setCatToolsOpen(false);
    try {
      const r = await integrationsApi.testConnection(conn.id);
      const details = r.toolDetails || (r.tools || []).map((n) => ({ name: n, description: '' }));
      const msg = r.ok ? ('Доступно инструментов: ' + details.length) : (r.error || 'Ошибка');
      setCatTestResult({ id: conn.id, ok: r.ok, msg, tools: r.tools || [], details });
    } catch (e: any) { setCatTestResult({ id: conn.id, ok: false, msg: e?.message || 'Ошибка', tools: [], details: [] }); } finally { setCatTesting(null); }
  };
  const acctLabel = (conn: any): string => (conn?.accountLabel && String(conn.accountLabel).trim()) ? String(conn.accountLabel) : 'Аккаунт';
  const statusPill = (st: string) => st === 'connected' ? <span className="rf-pill on"><span className="dot" />Подключено</span>
    : st === 'pending' ? <span className="rf-pill"><span className="dot" />Ожидание</span>
    : st === 'error' ? <span className="rf-pill err"><span className="dot" />Ошибка</span>
    : null;

  const tokenErrorMessage = (code: string): string => {
    const map: Record<string, string> = {
      token_required: 'Введите токен.',
      subdomain_and_token_required: 'Введите поддомен (адрес) и токен.',
      invalid_webhook_url: 'Некорректный URL вебхука. Формат: https://ваш-домен.bitrix24.ru/rest/1/xxxxxxxxxxxxxxxx',
      provider_does_not_support_token_auth: 'Этот сервис не поддерживает подключение по токену.',
      partner_and_user_token_and_company_id_required: 'Для YCLIENTS нужны partner_token, user_token и company_id.',
    };
    if (map[code]) return map[code];
    if (code.endsWith('_401') || code.endsWith('_403')) return 'Не удалось подключиться: токен недействителен или нет доступа.';
    if (code.endsWith('_no_base_url')) return 'Не удалось определить адрес сервиса.';
    return 'Не удалось подключиться: ' + code;
  };
  const openTokenForm = (id: string) => { haptic('light'); setTokenFormOpen((o) => o === id ? null : id); setTfSubdomain(''); setTfToken(''); setTfWebhookUrl(''); setTfUserToken(''); setTfCompanyId(''); setTfError(''); };
  const closeTokenForm = () => { setTokenFormOpen(null); setTfError(''); };
  const submitToken = async (providerId: string) => {
    haptic('medium'); setTfError(''); setTfSaving(true);
    try {
      const body: Record<string, string> = {};
      if (providerId === 'amocrm') { body.subdomain = tfSubdomain.trim(); body.token = tfToken.trim(); }
      else if (providerId === 'bitrix24') { body.webhook_url = tfWebhookUrl.trim(); }
      else if (providerId === 'moysklad') { body.token = tfToken.trim(); }
      else if (providerId === 'retailcrm') { body.subdomain = cleanSubdomain(tfSubdomain, '.retailcrm.ru'); body.token = tfToken.trim(); }
      else if (providerId === 'megaplan') { body.subdomain = cleanSubdomain(tfSubdomain, '.megaplan.ru'); body.token = tfToken.trim(); }
      else if (providerId === 'okocrm') { body.token = tfToken.trim(); }
      else if (providerId === 'envycrm') { body.subdomain = cleanSubdomain(tfSubdomain, '.envycrm.com'); body.token = tfToken.trim(); }
      else if (providerId === 'hubspot') { body.token = tfToken.trim(); }
      else if (providerId === 'pipedrive') { body.subdomain = cleanSubdomain(tfSubdomain, '.pipedrive.com'); body.token = tfToken.trim(); }
      else if (providerId === 'planfix') { body.subdomain = cleanSubdomain(tfSubdomain, '.planfix.com'); body.token = tfToken.trim(); }
      else if (providerId === 'yclients') { body.token = tfToken.trim(); body.user_token = tfUserToken.trim(); body.company_id = tfCompanyId.trim(); }
      await integrationsApi.connectToken(providerId, body);
      haptic('heavy'); closeTokenForm(); loadAll();
    } catch (e: any) {
      const msg = String(e?.message || '');
      const idx = msg.indexOf('{');
      let code = '';
      if (idx >= 0) { try { code = JSON.parse(msg.slice(idx)).error || ''; } catch { code = ''; } }
      setTfError(code ? tokenErrorMessage(code) : (msg || 'Ошибка подключения'));
    } finally { setTfSaving(false); }
  };
  const advApi = (conn: any): boolean => !!(conn?.meta && conn.meta['advanced_api'] === true);
  const advReadonly = (conn: any): boolean => !(conn?.meta && conn.meta['advanced_readonly'] === false);
  const toggleAdvancedApi = async (conn: any) => {
    haptic('medium'); setAdvBusy(conn.id);
    try { await integrationsApi.updateAdvanced(conn.id, { advanced_api: !advApi(conn) }); haptic('heavy'); loadAll(); } catch {} finally { setAdvBusy(null); }
  };
  const toggleAdvancedReadonly = async (conn: any) => {
    haptic('medium'); setAdvBusy(conn.id);
    try { await integrationsApi.updateAdvanced(conn.id, { advanced_readonly: !advReadonly(conn) }); haptic('heavy'); loadAll(); } catch {} finally { setAdvBusy(null); }
  };
  const cleanAmoSubdomain = (v: string): string => {
    let sd = v.trim();
    if (sd.indexOf('https://') === 0) sd = sd.slice(8);
    if (sd.indexOf('http://') === 0) sd = sd.slice(7);
    const idx = sd.indexOf('.amocrm.ru');
    if (idx >= 0) sd = sd.slice(0, idx);
    while (sd.length > 0 && sd.charAt(sd.length - 1) === '/') sd = sd.slice(0, -1);
    return sd;
  };
  // Универсальный аналог cleanAmoSubdomain для сервисов, где пользователь может
  // вставить как полный адрес (https://mycompany.retailcrm.ru), так и просто поддомен.
  const cleanSubdomain = (v: string, suffix: string): string => {
    let sd = v.trim();
    if (sd.indexOf('https://') === 0) sd = sd.slice(8);
    if (sd.indexOf('http://') === 0) sd = sd.slice(7);
    const idx = sd.indexOf(suffix);
    if (idx >= 0) sd = sd.slice(0, idx);
    while (sd.length > 0 && sd.charAt(sd.length - 1) === '/') sd = sd.slice(0, -1);
    return sd;
  };
  const TABS: { key: Tab; label: string; icon: any }[] = [
    { key: 'catalog', label: 'Каталог', icon: Layers }, { key: 'mcp', label: 'MCP', icon: Server },
    { key: 'webhook', label: 'Вебхуки', icon: Webhook }, { key: 'inbound', label: 'Триггеры', icon: Inbox },
  ];
  const switchTab = (k: Tab) => { haptic('light'); setTab(k); closeForm(); setTestResult(null); setCatTestResult(null); };

  return (
    <div className="rf-detail">
      <div className="rf-topbar">
        <button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button>
        <div className="rf-topbar-title">Связи</div>
        {!formOpen && tab !== 'catalog' && <button className="rf-back" onClick={openAdd}><Plus />Добавить</button>}
      </div>
      <div className="rf-detail-scroll">
        <div className="rf-info"><Plug /><div><b>{INTRO[tab].t}</b><br />{INTRO[tab].d}</div></div>
        <div className="rf-seg">
          {TABS.map((t) => { const Icon = t.icon; return (
            <button key={t.key} className={'rf-seg-btn' + (tab === t.key ? ' active' : '')} onClick={() => switchTab(t.key)}><Icon />{t.label}</button>
          ); })}
        </div>

        {formOpen && tab !== 'catalog' && (
          <div className="rf-card rf-acard rf-mb12">
            <div className="rf-seclabel">{editId ? 'Редактирование' : 'Новое подключение'}</div>
            {tab === 'mcp' && (<>
              <div className="rf-field"><label>Название</label><input className="rf-input" value={aName} onChange={(e) => setAName(e.target.value)} placeholder="My MCP Server" /></div>
              <div className="rf-field"><label>URL</label><input className="rf-input" value={aUrl} onChange={(e) => setAUrl(e.target.value)} placeholder="https://mcp.example.com/sse" /></div>
              <div className="rf-field"><label>Токен {editId ? '(оставьте пустым, чтобы не менять)' : '(необязательно)'}</label><input className="rf-input" value={aToken} onChange={(e) => setAToken(e.target.value)} placeholder="Bearer token" type="password" /></div>
              {editId && (<div className="rf-switch-row"><span>Активен</span><div className={'rf-switch' + (aEnabled ? ' on' : '')} onClick={() => setAEnabled(!aEnabled)} role="switch" aria-checked={aEnabled} /></div>)}
            </>)}
            {tab === 'inbound' && (<>
              <div className="rf-field"><label>Агент</label>
                <select className="rf-select" value={aAgentId} onChange={(e) => setAAgentId(e.target.value)}>
                  <option value="">— Выберите агента —</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <div className="rf-hint">Агент, который будет обрабатывать входящие события по этому вебхуку.</div>
              </div>
              <div className="rf-field"><label>Название (необязательно)</label><input className="rf-input" value={aName} onChange={(e) => setAName(e.target.value)} placeholder="Например: Триггер из CRM" /></div>
              {agents.length === 0 && <div className="rf-result err"><AlertCircle /><div>Сначала создайте хотя бы одного агента.</div></div>}
            </>)}
            {tab === 'webhook' && (<>
              <div className="rf-field"><label>Название</label><input className="rf-input" value={aName} onChange={(e) => setAName(e.target.value)} placeholder="Outbound webhook" /></div>
              <div className="rf-field"><label>URL</label><input className="rf-input" value={aUrl} onChange={(e) => setAUrl(e.target.value)} placeholder="https://example.com/hook" /></div>
              <div className="rf-field"><label>Метод</label><select className="rf-select" value={aMethod} onChange={(e) => setAMethod(e.target.value)}>{METHODS.map((m) => <option key={m} value={m}>{m}</option>)}</select></div>
              <div className="rf-field"><label>Описание (необязательно)</label><input className="rf-input" value={aDesc} onChange={(e) => setADesc(e.target.value)} placeholder="Для чего этот вебхук" /></div>
              {editId && (<div className="rf-switch-row"><span>Активен</span><div className={'rf-switch' + (aEnabled ? ' on' : '')} onClick={() => setAEnabled(!aEnabled)} role="switch" aria-checked={aEnabled} /></div>)}
            </>)}
            {error && <div className="rf-result err"><AlertCircle /><div>{error}</div></div>}
            <div className="rf-btn-row">
              <button className="rf-btn primary" onClick={submit} disabled={saving}>{saving ? <Loader2 className="spin" /> : editId ? <Save /> : <Plus />}{editId ? 'Сохранить' : 'Создать'}</button>
              <button className="rf-btn" onClick={closeForm}><X />Отмена</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="rf-loading"><div className="rf-spin" /><p>Загрузка…</p></div>
        ) : tab === 'mcp' ? (
          <div className="rf-rows rf-stagger">
            {mcpList.length === 0 ? (<div className="rf-empty"><div className="rf-empty-ic"><Server /></div><h3>MCP-серверов нет</h3><p>Подключите MCP-сервер кнопкой «Добавить»</p></div>
            ) : mcpList.map((m) => (
              <div key={m.id} className="rf-card rf-conn">
                <div className="rf-conn-row">
                  <div className="rf-row-ic"><Server /></div>
                  <div className="rf-conn-info"><div className="rf-conn-name">{m.name}</div><div className="rf-conn-sub">{m.url}{m.has_auth ? ' · auth' : ''}</div></div>
                  <span className={'rf-pill ' + (m.enabled ? 'on' : '')}><span className="dot" />{m.enabled ? 'ON' : 'OFF'}</span>
                </div>
                {m.last_error && <div className="rf-conn-err"><AlertCircle />{m.last_error}</div>}
                <div className="rf-acts">
                  <button className="rf-btn sm rf-grow" onClick={() => testMcp(m.id)} disabled={testing === m.id}>{testing === m.id ? <Loader2 className="spin" /> : <TestTube />}Тест</button>
                  <button className="rf-btn sm icon" title="Редактировать" onClick={() => editMcp(m)}><Edit3 /></button>
                  <button className="rf-btn sm danger icon" title="Удалить" onClick={() => delMcp(m.id)}><Trash2 /></button>
                </div>
                {testResult && testResult.id === m.id && (<>
                  <div className={'rf-result ' + (testResult.ok ? 'ok' : 'err')}>{testResult.ok ? <CheckCircle2 /> : <AlertCircle />}<div>{testResult.msg}</div></div>
                  {testResult.ok && testResult.details.length > 0 && (<>
                    <button className="rf-seclabel rf-mt10 rf-tooltoggle" onClick={() => setMcpToolsOpen((o) => !o)}>{mcpToolsOpen ? <ChevronDown /> : <ChevronRight />}<Wrench />&nbsp;Инструменты ({testResult.details.length})</button>
                    {mcpToolsOpen && <div className="rf-toollist">{testResult.details.map((td) => (
                      <div key={td.name} className="rf-toolrow"><div className="rf-toolrow-name">{td.name}</div>{td.description && <div className="rf-toolrow-desc">{td.description}</div>}</div>
                    ))}</div>}
                  </>)}
                  {testResult.ok && testResult.details.length === 0 && (<div className="rf-hint">Сервер не вернул список инструментов</div>)}
                </>)}
              </div>
            ))}
          </div>
        ) : tab === 'inbound' ? (
          <div className="rf-rows rf-stagger">
            <div className="rf-hint rf-mb12">Входящие вебхуки: внешний сервис шлёт запрос — выбранный агент его обрабатывает.</div>
            {inboundList.length === 0 ? (<div className="rf-empty"><div className="rf-empty-ic"><Inbox /></div><h3>Триггеров нет</h3><p>Создайте входящий вебхук</p></div>
            ) : inboundList.map((i) => { const open = openInbound === i.id; return (
              <div key={i.id} className={'rf-card rf-conn' + (open ? ' open' : '')}>
                <div className="rf-conn-row rf-conn-head" onClick={() => { haptic('light'); setOpenInbound(open ? null : i.id); }}>
                  <div className="rf-row-ic"><Inbox /></div>
                  <div className="rf-conn-info"><div className="rf-conn-name">{i.name || 'Без названия'}</div><div className="rf-conn-sub">Агент: {agentName(i.agent_id)}</div></div>
                  <span className={'rf-pill ' + (i.enabled ? 'on' : '')}><span className="dot" />{i.enabled ? 'ON' : 'OFF'}</span>
                  <ChevronDown className={'rf-agent-chev' + (open ? ' up' : '')} />
                </div>
                {open && (<>
                {i.token ? (
                  <div className="rf-tokenbox">
                    <div className="rf-tokenbox-label">URL для вызова</div>
                    <div className="rf-tokenrow"><code className="rf-token">{i.url}</code><button className="rf-btn sm icon" title="Копировать URL" onClick={() => copy(i.url, 'url-' + i.id)}>{copied === 'url-' + i.id ? <Check /> : <Copy />}</button></div>
                    <div className="rf-tokenbox-label rf-mt10">Токен</div>
                    <div className="rf-tokenrow"><code className="rf-token">{i.token}</code><button className="rf-btn sm icon" title="Копировать токен" onClick={() => copy(i.token!, 'tok-' + i.id)}>{copied === 'tok-' + i.id ? <Check /> : <Copy />}</button></div>
                  </div>
                ) : (
                  <div className="rf-hint rf-mt10">Токен этого триггера был создан ранее и не сохранён для показа. Чтобы получить копируемый токен, пересоздайте триггер.</div>
                )}
                <div className="rf-acts">
                  <button className="rf-btn sm rf-grow" onClick={() => toggleInbound(i)}>{i.enabled ? 'Выключить' : 'Включить'}</button>
                  <button className="rf-btn sm icon" title="Редактировать" onClick={() => editInbound(i)}><Edit3 /></button>
                  <button className="rf-btn sm danger icon" title="Удалить" onClick={() => delInbound(i.id)}><Trash2 /></button>
                </div>
                </>)}
              </div>
            ); })}
          </div>
        ) : tab === 'webhook' ? (
          <div className="rf-rows rf-stagger">
            {webhookList.length === 0 ? (<div className="rf-empty"><div className="rf-empty-ic"><Webhook /></div><h3>Вебхуков нет</h3><p>Создайте исходящий вебхук</p></div>
            ) : webhookList.map((w) => (
              <div key={w.id} className="rf-card rf-conn">
                <div className="rf-conn-row">
                  <div className="rf-row-ic"><Webhook /></div>
                  <div className="rf-conn-info"><div className="rf-conn-name">{w.name}</div><div className="rf-conn-sub">{w.method} · {w.url}{w.description ? <><br />{w.description}</> : null}</div></div>
                  <span className={'rf-pill ' + (w.enabled ? 'on' : '')}><span className="dot" />{w.enabled ? 'ON' : 'OFF'}</span>
                </div>
                <div className="rf-acts">
                  <button className="rf-btn sm rf-grow" onClick={() => editWebhook(w)}><Edit3 />Редактировать</button>
                  <button className="rf-btn sm danger icon" title="Удалить" onClick={() => delWebhook(w.id)}><Trash2 /></button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rf-rows rf-stagger">
            {catalog.length > 0 && (
              <div className="rf-catfilter">
                {['all', 'connected', ...CAT_ORDER.filter((cat) => catalog.some((c) => (c.category || 'other') === cat))].map((cat) => (
                  <button key={cat} className={'rf-catchip' + (catFilter === cat ? ' on' : '')} onClick={() => { haptic('light'); setCatFilter(cat); }}>{cat === 'all' ? 'Все' : cat === 'connected' ? 'Подключённые' : (CAT_LABELS[cat] || cat)}</button>
                ))}
              </div>
            )}
            {catalog.length === 0 ? (<div className="rf-empty"><div className="rf-empty-ic"><Layers /></div><h3>Каталог пуст</h3><p>Доступные интеграции появятся позже</p></div>
            ) : catalog.filter((c) => catFilter === 'all' || (catFilter === 'connected' ? connList(c).some((x: any) => x.status === 'connected') : (c.category || 'other') === catFilter)).map((c) => { const list = connList(c); if (c.telegramConnect) return (<TelegramCard key={c.id} prov={c} openLink={openLink} />); return (
              <div key={c.id} className="rf-card rf-conn">
                <div className="rf-conn-row">
                  <div className="rf-row-ic"><Plug /></div>
                  <div className="rf-conn-info"><div className="rf-conn-name">{c.name}</div><div className="rf-conn-sub">{c.description}</div></div>
                  {list.length > 0 && <span className="rf-pill on"><span className="dot" />{list.length} {list.length === 1 ? 'аккаунт' : 'акк.'}</span>}
                </div>
                {list.length === 0 ? (
                  <div className="rf-hint">Нет подключённых аккаунтов</div>
                ) : list.map((conn: any) => (
                  <div key={conn.id} className="rf-acct">
                    <div className="rf-conn-row rf-acct-row">
                      <div className="rf-conn-info">
                        <div className="rf-conn-name">{acctLabel(conn)}</div>
                        {conn.meta && (conn.meta.owner_email || conn.meta.login) ? <div className="rf-conn-sub">{String(conn.meta.owner_email || conn.meta.login)}</div> : null}
                      </div>
                      {statusPill(conn.status)}
                    </div>
                    {conn.lastError && <div className="rf-conn-err"><AlertCircle />{String(conn.lastError)}</div>}
                    <div className="rf-acts">
                      {conn.status === 'connected' && (
                        <button className="rf-btn sm rf-grow" onClick={() => testCatalog(conn)} disabled={catTesting === conn.id}>{catTesting === conn.id ? <Loader2 className="spin" /> : <TestTube />}Тест</button>
                      )}
                      <button className="rf-btn sm danger icon" title="Отключить аккаунт" onClick={() => disconnectCatalog(conn)} disabled={connecting === conn.id}>{connecting === conn.id ? <Loader2 className="spin" /> : <Trash2 />}</button>
                    </div>
                    {conn.status === 'connected' && c.advancedApiSupported && (
                      <div className="rf-acct rf-mt10">
                        <div className="rf-switch-row">
                          <span>Продвинутый режим: полный доступ к API</span>
                          <div className={'rf-switch' + (advApi(conn) ? ' on' : '')} onClick={() => advBusy !== conn.id && toggleAdvancedApi(conn)} role="switch" aria-checked={advApi(conn)} />
                        </div>
                        <div className="rf-hint">Добавляет универсальный инструмент, который может вызывать любые методы вашего CRM в рамках прав вашего токена. У сырых вызовов нет умных ограничений — выше риск нежелательных или необратимых действий. В большинстве случаев достаточно стандартных инструментов.</div>
                        {advApi(conn) && (<>
                          <div className="rf-switch-row rf-mt10">
                            <span>Только чтение</span>
                            <div className={'rf-switch' + (advReadonly(conn) ? ' on' : '')} onClick={() => advBusy !== conn.id && toggleAdvancedReadonly(conn)} role="switch" aria-checked={advReadonly(conn)} />
                          </div>
                          <div className="rf-hint">Универсальный инструмент сможет только читать данные (списки и карточки) и не сможет создавать, изменять или удалять записи. Выключите, если хотите разрешить агенту изменять данные в CRM — но учтите: это может привести к необратимым последствиям.</div>
                        </>)}
                      </div>
                    )}
                    {catTestResult && catTestResult.id === conn.id && (
                      <>
                        <div className={'rf-result ' + (catTestResult.ok ? 'ok' : 'err')}>{catTestResult.ok ? <CheckCircle2 /> : <AlertCircle />}<div>{catTestResult.msg}</div></div>
                        {catTestResult.ok && catTestResult.details.length > 0 && (
                          <>
                            <button className="rf-seclabel rf-mt10 rf-tooltoggle" onClick={() => setCatToolsOpen((o) => !o)}>{catToolsOpen ? <ChevronDown /> : <ChevronRight />}<Wrench />&nbsp;Инструменты ({catTestResult.details.length})</button>
                            {catToolsOpen && <div className="rf-toollist">{catTestResult.details.map((td) => (
                              <div key={td.name} className="rf-toolrow"><div className="rf-toolrow-name">{td.name}</div>{td.description && <div className="rf-toolrow-desc">{td.description}</div>}</div>
                            ))}</div>}
                          </>
                        )}
                        {catTestResult.ok && catTestResult.details.length === 0 && (<div className="rf-hint">Инструменты не найдены</div>)}
                      </>
                    )}
                  </div>
                ))}
                {Array.isArray(c.setupNote) && c.setupNote.length > 0 && (<>
                  <button className="rf-seclabel rf-mt10 rf-tooltoggle" onClick={() => { haptic('light'); setSetupOpen((o) => o === c.id ? null : c.id); }}>{setupOpen === c.id ? <ChevronDown /> : <ChevronRight />}<Info />&nbsp;Как подключить</button>
                  {setupOpen === c.id && <div className="rf-info rf-mt10"><Info /><div>{c.setupNote.map((line: string, i: number) => <div key={i} style={ { marginBottom: 4 } }>{i + 1}. {line}</div>)}</div></div>}
                </>)}
                {tokenFormOpen === c.id && (
                  <div className="rf-acct rf-mt10">
                    <div className="rf-seclabel" style={ { marginTop: 0 } }>Подключение по токену</div>
                    {c.id === 'amocrm' && (<>
                      <div className="rf-field"><label>Поддомен amoCRM</label>
                        <input className="rf-input" value={tfSubdomain} onChange={(e) => setTfSubdomain(e.target.value)} placeholder="mycompany" />
                        <div className="rf-hint">Например, если адрес вашего amoCRM — mycompany.amocrm.ru, введите mycompany.</div>
                        <button className="rf-btn sm rf-mt10" onClick={() => { const sd = cleanAmoSubdomain(tfSubdomain); if (!sd) { setTfError('Сначала введите поддомен.'); return; } setTfError(''); openLink('https://' + sd + '.amocrm.ru/settings/api/'); }}><ExternalLink />Получить токен</button>
                      </div>
                      <div className="rf-field"><label>Долгосрочный токен</label><textarea className="rf-textarea" value={tfToken} onChange={(e) => setTfToken(e.target.value)} placeholder="Вставьте долгосрочный токен" /></div>
                    </>)}
                    {c.id === 'bitrix24' && (<>
                      <div className="rf-field"><label>URL входящего вебхука</label><input className="rf-input" value={tfWebhookUrl} onChange={(e) => setTfWebhookUrl(e.target.value)} placeholder="https://yourcompany.bitrix24.ru/rest/1/xxxxxxxxxxxxxxxx" /></div>
                      <button className="rf-btn sm rf-mt10" onClick={() => openLink('https://apidocs.bitrix24.ru/first-steps/first-rest-api-call.html')}><ExternalLink />Как получить вебхук (инструкция Bitrix24)</button>
                    </>)}
                    {c.id === 'moysklad' && (<>
                      <div className="rf-field"><label>API-токен</label>
                        <input className="rf-input" value={tfToken} onChange={(e) => setTfToken(e.target.value)} placeholder="API-токен МойСклад" type="password" />
                        <button className="rf-btn sm rf-mt10" onClick={() => openLink('https://online.moysklad.ru/app/')}><ExternalLink />Получить токен</button>
                      </div>
                    </>)}
                    {c.id === 'retailcrm' && (<>
                      <div className="rf-field"><label>Адрес RetailCRM</label>
                        <input className="rf-input" value={tfSubdomain} onChange={(e) => setTfSubdomain(e.target.value)} placeholder="myshop" />
                        <div className="rf-hint">Например, если адрес вашего RetailCRM — myshop.retailcrm.ru, введите myshop.</div>
                      </div>
                      <div className="rf-field"><label>API-ключ</label>
                        <input className="rf-input" value={tfToken} onChange={(e) => setTfToken(e.target.value)} placeholder="API-ключ RetailCRM" type="password" />
                        <button className="rf-btn sm rf-mt10" onClick={() => { const sd = cleanSubdomain(tfSubdomain, '.retailcrm.ru'); if (!sd) { setTfError('Сначала введите адрес RetailCRM.'); return; } setTfError(''); openLink('https://' + sd + '.retailcrm.ru/admin/'); }}><ExternalLink />Получить токен</button>
                      </div>
                    </>)}
                    {c.id === 'megaplan' && (<>
                      <div className="rf-field"><label>Адрес Мегаплана</label>
                        <input className="rf-input" value={tfSubdomain} onChange={(e) => setTfSubdomain(e.target.value)} placeholder="mycompany" />
                        <div className="rf-hint">Например, если адрес вашего Мегаплана — mycompany.megaplan.ru, введите mycompany.</div>
                      </div>
                      <div className="rf-field"><label>API-токен</label>
                        <input className="rf-input" value={tfToken} onChange={(e) => setTfToken(e.target.value)} placeholder="API-токен Мегаплана" type="password" />
                        <button className="rf-btn sm rf-mt10" onClick={() => { const sd = cleanSubdomain(tfSubdomain, '.megaplan.ru'); if (!sd) { setTfError('Сначала введите адрес Мегаплана.'); return; } setTfError(''); openLink('https://' + sd + '.megaplan.ru/'); }}><ExternalLink />Открыть Мегаплан (получить токен)</button>
                      </div>
                    </>)}
                    {c.id === 'okocrm' && (<>
                      <div className="rf-field"><label>Токен для API</label>
                        <input className="rf-input" value={tfToken} onChange={(e) => setTfToken(e.target.value)} placeholder="Токен OkoCRM" type="password" />
                        <button className="rf-btn sm rf-mt10" onClick={() => openLink('https://app.okocrm.com/')}><ExternalLink />Открыть OkoCRM (получить токен)</button>
                      </div>
                    </>)}
                    {c.id === 'envycrm' && (<>
                      <div className="rf-field"><label>Адрес EnvyCRM</label>
                        <input className="rf-input" value={tfSubdomain} onChange={(e) => setTfSubdomain(e.target.value)} placeholder="mycompany" />
                        <div className="rf-hint">Например, если адрес вашего EnvyCRM — mycompany.envycrm.com, введите mycompany.</div>
                      </div>
                      <div className="rf-field"><label>Ключ API</label>
                        <input className="rf-input" value={tfToken} onChange={(e) => setTfToken(e.target.value)} placeholder="Ключ API EnvyCRM" type="password" />
                        <button className="rf-btn sm rf-mt10" onClick={() => { const sd = cleanSubdomain(tfSubdomain, '.envycrm.com'); if (!sd) { setTfError('Сначала введите адрес EnvyCRM.'); return; } setTfError(''); openLink('https://' + sd + '.envycrm.com/'); }}><ExternalLink />Открыть EnvyCRM (получить ключ)</button>
                      </div>
                    </>)}
                    {c.id === 'hubspot' && (<>
                      <div className="rf-field"><label>Private App Access Token</label>
                        <input className="rf-input" value={tfToken} onChange={(e) => setTfToken(e.target.value)} placeholder="pat-na1-…" type="password" />
                        <div className="rf-hint">Токен Private App в HubSpot. Начинается с «pat-». Не путайте с API Key — он устарел.</div>
                        <button className="rf-btn sm rf-mt10" onClick={() => openLink('https://app.hubspot.com/l/private-apps')}><ExternalLink />Открыть HubSpot → Private Apps</button>
                      </div>
                    </>)}
                    {c.id === 'pipedrive' && (<>
                      <div className="rf-field"><label>Поддомен Pipedrive</label>
                        <input className="rf-input" value={tfSubdomain} onChange={(e) => setTfSubdomain(e.target.value)} placeholder="mycompany" />
                        <div className="rf-hint">Например, если адрес — mycompany.pipedrive.com, введите mycompany.</div>
                      </div>
                      <div className="rf-field"><label>Personal API Token</label>
                        <input className="rf-input" value={tfToken} onChange={(e) => setTfToken(e.target.value)} placeholder="API-токен Pipedrive" type="password" />
                        <button className="rf-btn sm rf-mt10" onClick={() => { const sd = cleanSubdomain(tfSubdomain, '.pipedrive.com'); const url = sd ? ('https://' + sd + '.pipedrive.com/settings/api') : 'https://app.pipedrive.com/settings/api'; openLink(url); }}><ExternalLink />Получить токен</button>
                      </div>
                    </>)}
                    {c.id === 'planfix' && (<>
                      <div className="rf-field"><label>Поддомен Planfix</label>
                        <input className="rf-input" value={tfSubdomain} onChange={(e) => setTfSubdomain(e.target.value)} placeholder="mycompany" />
                        <div className="rf-hint">Например, если адрес — mycompany.planfix.com или mycompany.planfix.ru, введите mycompany.</div>
                      </div>
                      <div className="rf-field"><label>REST API-токен</label>
                        <input className="rf-input" value={tfToken} onChange={(e) => setTfToken(e.target.value)} placeholder="Токен Planfix REST API" type="password" />
                        <button className="rf-btn sm rf-mt10" onClick={() => { const sd = cleanSubdomain(tfSubdomain, '.planfix.com'); const alt = cleanSubdomain(tfSubdomain, '.planfix.ru'); const host = sd || alt; if (!host) { setTfError('Сначала введите поддомен Planfix.'); return; } setTfError(''); openLink('https://' + host + '.planfix.com/account/#tab=api'); }}><ExternalLink />Открыть Planfix → Настройки API</button>
                      </div>
                    </>)}
                    {c.id === 'yclients' && (<>
                      <div className="rf-field"><label>Partner token</label>
                        <input className="rf-input" value={tfToken} onChange={(e) => setTfToken(e.target.value)} placeholder="Партнёрский токен YCLIENTS" type="password" />
                        <div className="rf-hint">Партнёрский токен выдаётся YCLIENTS в кабинете партнёра.</div>
                      </div>
                      <div className="rf-field"><label>User token</label>
                        <input className="rf-input" value={tfUserToken} onChange={(e) => setTfUserToken(e.target.value)} placeholder="Пользовательский токен" type="password" />
                        <div className="rf-hint">Получается POST-запросом в /api/v1/auth с логином/паролем владельца филиала.</div>
                      </div>
                      <div className="rf-field"><label>Company ID</label>
                        <input className="rf-input" value={tfCompanyId} onChange={(e) => setTfCompanyId(e.target.value)} placeholder="Например, 123456" />
                        <div className="rf-hint">ID филиала. Виден в адресной строке YCLIENTS.</div>
                        <button className="rf-btn sm rf-mt10" onClick={() => openLink('https://developers.yclients.com/ru/')}><ExternalLink />Документация YCLIENTS API</button>
                      </div>
                    </>)}
                    {tfError && <div className="rf-result err"><AlertCircle /><div>{tfError}</div></div>}
                    <div className="rf-btn-row">
                      <button className="rf-btn primary" onClick={() => submitToken(c.id)} disabled={tfSaving}>{tfSaving ? <Loader2 className="spin" /> : <Save />}Подключить</button>
                      <button className="rf-btn" onClick={closeTokenForm}><X />Отмена</button>
                    </div>
                  </div>
                )}
                <div className="rf-acts rf-mt10">
                  {c.tokenAuthSupported && (
                    <button className="rf-btn sm primary rf-grow" onClick={() => openTokenForm(c.id)}><Key />{list.length > 0 ? 'Подключить ещё' : 'Подключить по токену'}</button>
                  )}
                  {c.oauthProvider && c.canConnect && c.id !== 'amocrm' && c.id !== 'bitrix24' && (
                    <button className={'rf-btn sm rf-grow' + (c.tokenAuthSupported ? '' : ' primary')} onClick={() => connectCatalog(c)} disabled={connecting === c.id}>{connecting === c.id ? <Loader2 className="spin" /> : <Plus />}{'Через OAuth'}</button>
                  )}
                  {!c.canConnect && (
                    <span className="rf-pill rf-grow"><span className="dot" />Скоро</span>
                  )}
                </div>
              </div>
            ); })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// TelegramCard — простая сворачиваемая карточка общей интеграции Telegram.
//  Единый список каналов + сворачиваемый блок «Добавить канал»
//  (бот платформы + свои боты). Минимум вложенности, без наслоений.
// ============================================================
function TelegramCard({ prov, openLink }: { prov: any; openLink: (u: string) => void }) {
  const [ov, setOv] = useState<import('../../api/integrationsApi').TgOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [ownOpen, setOwnOpen] = useState(false);
  const [tokenFormOpen, setTokenFormOpen] = useState(false);
  const [token, setToken] = useState('');
  const [err, setErr] = useState('');
  const [hint, setHint] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);

  const reload = async () => { try { const r = await integrationsApi.tgOverview(); setOv(r); } catch { /* ignore */ } finally { setLoading(false); } };
  useEffect(() => { reload(); }, []);
  useEffect(() => { if (!waiting) return; const t = setInterval(reload, 4000); return () => clearInterval(t); }, [waiting]);

  const platformConn = ov?.connections.find((c) => c.mode === 'platform') || null;
  const customConns = (ov?.connections || []).filter((c) => c.mode === 'custom');
  const resources = ov?.resources || [];
  const count = resources.length;
  const plural = count === 1 ? 'канал' : (count >= 2 && count <= 4 ? 'канала' : 'каналов');

  const botLabel = (connId: string): string => {
    const c = ov?.connections.find((x) => x.id === connId);
    if (!c) return '';
    return c.mode === 'platform' ? 'бот платформы' : ('@' + (c.botUsername || ''));
  };

  const statusPill = (r: any) => r.status === 'ready' && r.canPost ? <span className="rf-pill on"><span className="dot" />Можно публиковать</span>
    : r.status === 'no_rights' ? <span className="rf-pill err"><span className="dot" />Нет прав</span>
    : r.status === 'bot_removed' ? <span className="rf-pill err"><span className="dot" />Бот удалён</span>
    : <span className="rf-pill"><span className="dot" />Ожидание</span>;

  const connectPlatform = async () => {
    haptic('medium'); setBusy('platform'); setErr('');
    try {
      const r = await integrationsApi.tgConnectPlatform();
      if (r?.deepLink) { openLink(r.deepLink); setWaiting(true); }
      await reload();
    } catch (e: any) {
      const msg = String(e?.message || '');
      setErr(msg.includes('platform_bot') ? 'Бот платформы не настроен.' : 'Не удалось начать подключение.');
    } finally { setBusy(null); }
  };
  const connectCustom = async () => {
    haptic('medium'); setBusy('custom'); setErr('');
    try {
      await integrationsApi.tgConnectCustom(token.trim());
      setToken(''); setTokenFormOpen(false); await reload();
    } catch (e: any) {
      const msg = String(e?.message || '');
      if (msg.includes('invalid_token_format')) setErr('Это не похоже на токен бота. Скопируйте токен у @BotFather целиком.');
      else if (msg.includes('invalid_token')) setErr('Токен не подошёл. Проверьте, что скопировали его целиком.');
      else setErr('Не удалось подключить бота.');
    } finally { setBusy(null); }
  };
  const discover = async (id: string) => {
    haptic('light'); setBusy('discover-' + id); setErr(''); setHint(null);
    try { const r = await integrationsApi.tgDiscover(id); setHint(r.hint); await reload(); }
    catch (e: any) {
      const msg = String(e?.message || '');
      if (msg.includes('webhook_conflict')) setErr('Похоже, этот бот уже подключён к другому сервису (установлен webhook). Создайте отдельного бота или отключите старое подключение.');
      else setErr('Не удалось найти каналы. Добавьте бота в канал и отправьте там сообщение, затем повторите.');
    } finally { setBusy(null); }
  };
  const checkRes = async (id: string) => { haptic('light'); setBusy('check-' + id); try { await integrationsApi.tgCheckResource(id); await reload(); } catch { /* */ } finally { setBusy(null); } };
  const delRes = async (id: string) => { haptic('heavy'); setBusy('del-' + id); try { await integrationsApi.tgDeleteResource(id); await reload(); } catch { /* */ } finally { setBusy(null); } };
  const delConn = async (id: string) => { haptic('heavy'); setBusy('delc-' + id); try { await integrationsApi.tgDeleteConnection(id); await reload(); } catch { /* */ } finally { setBusy(null); } };

  const renderChannel = (r: any) => (
    <div key={r.id} className="rf-acct">
      <div className="rf-conn-row rf-acct-row">
        <div className="rf-conn-info">
          <div className="rf-conn-name">{r.chatType === 'channel' ? 'Канал' : 'Группа'}: {r.title || r.username || 'без названия'}</div>
          <div className="rf-conn-sub">{botLabel(r.connectionId)}</div>
        </div>
        {statusPill(r)}
      </div>
      {r.status === 'no_rights' && <div className="rf-conn-err"><AlertCircle />Дайте боту право «Публикация сообщений» (для каналов) или права администратора.</div>}
      {r.status === 'bot_removed' && <div className="rf-conn-err"><AlertCircle />Бот удалён из канала. Добавьте его снова и нажмите «Проверить».</div>}
      <div className="rf-acts">
        <button className="rf-btn sm rf-grow" onClick={() => checkRes(r.id)} disabled={busy === 'check-' + r.id}>{busy === 'check-' + r.id ? <Loader2 className="spin" /> : <TestTube />}Проверить</button>
        <button className="rf-btn sm danger icon" title="Отключить канал" onClick={() => delRes(r.id)} disabled={busy === 'del-' + r.id}>{busy === 'del-' + r.id ? <Loader2 className="spin" /> : <Trash2 />}</button>
      </div>
    </div>
  );

  return (
    <div className="rf-card rf-conn">
      <div className="rf-conn-row rf-conn-head" onClick={() => { haptic('light'); setOpen((o) => !o); }}>
        <div className="rf-row-ic"><Plug /></div>
        <div className="rf-conn-info">
          <div className="rf-conn-name">Telegram</div>
          <div className="rf-conn-sub">{count > 0 ? `${count} ${plural} подключено` : 'Публикуйте в каналы и группы'}</div>
        </div>
        {count > 0 && <span className="rf-pill on"><span className="dot" />{count}</span>}
        <ChevronDown className={'rf-agent-chev' + (open ? ' up' : '')} />
      </div>

      {open && (loading ? <div className="rf-hint">Загрузка…</div> : (
        <>
          {resources.length > 0 ? (
            <>
              <div className="rf-seclabel">Подключённые каналы</div>
              {resources.map(renderChannel)}
            </>
          ) : (
            <div className="rf-hint">Пока нет подключённых каналов. Добавьте первый ниже.</div>
          )}

          <button className="rf-seclabel rf-tooltoggle rf-mt10" onClick={() => { haptic('light'); setAddOpen((o) => !o); }}>{addOpen ? <ChevronDown /> : <ChevronRight />}<Plus />&nbsp;Добавить канал</button>
          {addOpen && (
            <>
              <div className="rf-hint">Рекомендуем: через нашего бота — без токена и технических деталей. Просто добавьте бота админом в канал.</div>
              {!ov?.platformBotConfigured && <div className="rf-conn-err"><Info />Бот платформы не настроен. Используйте своего бота ниже.</div>}
              {platformConn ? (
                <button className="rf-btn rf-grow rf-mt10" onClick={() => { if (ov?.platformBotUsername) { haptic('light'); openLink('https://t.me/' + ov.platformBotUsername); setWaiting(true); } }} disabled={!ov?.platformBotUsername}><ExternalLink />Перейти к боту платформы</button>
              ) : (
                <button className="rf-btn primary rf-grow rf-mt10" onClick={connectPlatform} disabled={busy === 'platform' || !ov?.platformBotConfigured}>{busy === 'platform' ? <Loader2 className="spin" /> : <Plus />}Подключить через бота платформы</button>
              )}
              {waiting && ov?.platformBotUsername && <div className="rf-hint rf-mt10">Открыли @{ov.platformBotUsername}. Добавьте его админом в канал — он появится здесь сам. Можно добавить несколько каналов.</div>}

              <button className="rf-seclabel rf-tooltoggle rf-mt10" onClick={() => { haptic('light'); setOwnOpen((o) => !o); }}>{ownOpen ? <ChevronDown /> : <ChevronRight />}<Key />&nbsp;Через своего бота{customConns.length > 0 ? ` (${customConns.length})` : ''}</button>
              {ownOpen && (
                <>
                  {customConns.map((cc) => {
                    const cnt = resources.filter((r) => r.connectionId === cc.id).length;
                    return (
                      <div key={cc.id} className="rf-acct">
                        <div className="rf-conn-row rf-acct-row">
                          <div className="rf-conn-info"><div className="rf-conn-name">@{cc.botUsername || ''}</div><div className="rf-conn-sub">{cnt > 0 ? `${cnt} кан.` : 'нет каналов'}</div></div>
                        </div>
                        <div className="rf-acts">
                          <button className="rf-btn sm rf-grow" onClick={() => discover(cc.id)} disabled={busy === 'discover-' + cc.id}>{busy === 'discover-' + cc.id ? <Loader2 className="spin" /> : <TestTube />}Найти канал</button>
                          <button className="rf-btn sm danger icon" title="Удалить бота" onClick={() => delConn(cc.id)} disabled={busy === 'delc-' + cc.id}>{busy === 'delc-' + cc.id ? <Loader2 className="spin" /> : <Trash2 />}</button>
                        </div>
                      </div>
                    );
                  })}
                  {!tokenFormOpen ? (
                    <button className="rf-btn sm rf-grow rf-mt10" onClick={() => { haptic('light'); setTokenFormOpen(true); setErr(''); }}><Plus />Добавить своего бота</button>
                  ) : (
                    <div className="rf-acct rf-mt10">
                      <div className="rf-hint">Откройте @BotFather → создайте бота → скопируйте токен → вставьте ниже.</div>
                      <div className="rf-field rf-mt10"><label>Токен бота</label><input className="rf-input" value={token} onChange={(e) => setToken(e.target.value)} placeholder="123456:ABC-DEF…" type="password" /></div>
                      <div className="rf-btn-row">
                        <button className="rf-btn primary" onClick={connectCustom} disabled={busy === 'custom' || !token.trim()}>{busy === 'custom' ? <Loader2 className="spin" /> : <Save />}Подключить</button>
                        <button className="rf-btn" onClick={() => openLink('https://t.me/BotFather')}><ExternalLink />BotFather</button>
                        <button className="rf-btn" onClick={() => { setTokenFormOpen(false); setToken(''); setErr(''); }}><X />Отмена</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {err && <div className="rf-result err"><AlertCircle /><div>{err}</div></div>}
          {hint && <div className="rf-hint rf-mt10">{hint}</div>}

          {platformConn && (
            <div className="rf-acts rf-mt10">
              <button className="rf-btn sm danger rf-grow" onClick={() => delConn(platformConn.id)} disabled={busy === 'delc-' + platformConn.id}>{busy === 'delc-' + platformConn.id ? <Loader2 className="spin" /> : <Trash2 />}Удалить связь с ботом платформы</button>
            </div>
          )}
        </>
      ))}
    </div>
  );
}
